import {
    Button,
    Container,
    Dropdown,
    Form,
    Input,
    InputPlaceholder,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Toggle,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Deferred, useForm, usePage } from '@inertiajs/react';
import Navigation from './components/Navigation';

const EntitySettingView = () => {
    const { auth, settings, questionnaire_application_period, contacts } =
        usePage().props;
    const { data, setData, patch, processing, transform, isDirty } = useForm(
        settings || []
    );

    const entities = auth?.customer?.entities || [];

    const settingFor = entityId =>
        data?.find(setting => setting?.base_customer_entity_id === entityId);

    const notificationsEnabledForAll =
        entities.length > 0 &&
        entities.every(entity => settingFor(entity.id)?.notifications_enabled);

    const handleAll = (name, value) => {
        setData(
            entities.map(entity => ({
                ...(settingFor(entity.id) || {
                    base_customer_entity_id: entity.id,
                }),
                [name]: value,
            }))
        );
    };

    const handleChange = (entityId, name, value) => {
        if (!settingFor(entityId)) {
            setData([
                ...(data || []),
                { base_customer_entity_id: entityId, [name]: value },
            ]);

            return;
        }

        setData(
            data.map(setting =>
                setting?.base_customer_entity_id === entityId
                    ? { ...setting, [name]: value }
                    : setting
            )
        );
    };

    const handleSubmit = () => {
        transform(data => ({ settings: data }));

        patch(
            `/customers/${auth?.user?.selected_customer_id}/services/sroi/settings/entities`,
            { preserveScroll: true, only: ['settings'] }
        );
    };

    return (
        <Container fluid>
            <Navigation />

            <Segment>
                <SegmentHeader
                    header='Entiteiten instellingen'
                    subheader='Hier kunt u de instellingen van de entiteiten aanpassen.'
                />
                <SegmentContent>
                    <Form onSubmit={handleSubmit}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                    <TableHeaderCell collapsing>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                Aanvraagperiode
                                                <InformationCircleIcon className='ml-2 inline-flex size-4' />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <ul>
                                                    <li>
                                                        Vul hier het aantal
                                                        maanden dat wij een
                                                        werknemer met
                                                        terugwerkende kracht
                                                        mogen benaderen (vanaf
                                                        de startdatum van het
                                                        dienstverband);
                                                    </li>
                                                    <li>{`De standaardwaarde is ingesteld op ${questionnaire_application_period} maanden (gelijkgetrokken met LKV);`}</li>
                                                    <li>
                                                        Stel de waarde in op -1
                                                        als alle werknemers die
                                                        in dienst zijn, moeten
                                                        worden benaderd.
                                                    </li>
                                                </ul>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHeaderCell>
                                    <TableHeaderCell collapsing>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                Meldingen inschakelen
                                                <InformationCircleIcon className='ml-2 inline-flex size-4' />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Indien ingeschakeld, wordt de
                                                geselecteerde contactpersoon
                                                wekelijks een e-mail gestuurd
                                                als er werknemers zijn waarvan
                                                de sroi binnen 3 maanden
                                                verlopen.
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHeaderCell>
                                    <TableHeaderCell
                                        textAlign='right'
                                        collapsing
                                    >
                                        Contactpersoon
                                    </TableHeaderCell>
                                </TableRow>
                                <TableRow>
                                    <TableHeaderCell>
                                        Instellen voor alle entiteiten
                                    </TableHeaderCell>
                                    <TableHeaderCell collapsing>
                                        <Input
                                            type='number'
                                            onChange={event =>
                                                handleAll(
                                                    'questionnaire_application_period',
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </TableHeaderCell>
                                    <TableHeaderCell
                                        collapsing
                                        textAlign='center'
                                    >
                                        <Toggle
                                            checked={notificationsEnabledForAll}
                                            onChange={checked =>
                                                handleAll(
                                                    'notifications_enabled',
                                                    checked
                                                )
                                            }
                                        />
                                    </TableHeaderCell>
                                    <TableHeaderCell
                                        textAlign='right'
                                        collapsing
                                    >
                                        <Deferred
                                            data='contacts'
                                            fallback={<InputPlaceholder />}
                                        >
                                            <Dropdown
                                                options={contacts || []}
                                                value={''}
                                                onChange={value =>
                                                    handleAll(
                                                        'contact_id',
                                                        value
                                                    )
                                                }
                                            />
                                        </Deferred>
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {entities.map(entity => (
                                    <TableRow key={entity.id}>
                                        <TableCell>{entity.name}</TableCell>
                                        <TableCell collapsing>
                                            <Input
                                                type='number'
                                                placeholder={
                                                    questionnaire_application_period
                                                }
                                                value={
                                                    settingFor(entity.id)
                                                        ?.questionnaire_application_period ??
                                                    ''
                                                }
                                                onChange={event =>
                                                    handleChange(
                                                        entity.id,
                                                        'questionnaire_application_period',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            collapsing
                                            textAlign='center'
                                        >
                                            <Toggle
                                                checked={
                                                    settingFor(entity.id)
                                                        ?.notifications_enabled ||
                                                    false
                                                }
                                                onChange={checked =>
                                                    handleChange(
                                                        entity.id,
                                                        'notifications_enabled',
                                                        checked
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell collapsing>
                                            <Deferred
                                                data='contacts'
                                                fallback={<InputPlaceholder />}
                                            >
                                                <Dropdown
                                                    options={contacts || []}
                                                    value={
                                                        settingFor(entity.id)
                                                            ?.contact_id || ''
                                                    }
                                                    onChange={value =>
                                                        handleChange(
                                                            entity.id,
                                                            'contact_id',
                                                            value
                                                        )
                                                    }
                                                />
                                            </Deferred>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <div className='mt-4 flex justify-end'>
                            <Button
                                variant='primary'
                                type='submit'
                                loading={processing}
                                disabled={!isDirty}
                            >
                                Opslaan
                            </Button>
                        </div>
                    </Form>
                </SegmentContent>
            </Segment>
        </Container>
    );
};

export default EntitySettingView;
